import type { FilePickerItem } from "../types/onedrive";

/**
 * A file pulled from a cloud picker together with the folder path it came from,
 * e.g. `{ file, relativePath: "Awards/2024/prize.pdf" }`. Structurally a
 * `CollectedFile`, so picked trees flow into the same upload path as local
 * folder drops.
 */
export type PickedItem = {
  file: File;
  relativePath: string;
};

const joinPath = (prefix: string, name: string) =>
  prefix ? `${prefix}/${name}` : name;

export const GOOGLE_FOLDER_MIME = "application/vnd.google-apps.folder";

export type DriveEntry = {
  id: string;
  name: string;
  mimeType: string;
};

export const isDriveFolder = (entry: { mimeType?: string }): boolean =>
  entry.mimeType === GOOGLE_FOLDER_MIME;

/**
 * Native Google formats (Docs, Sheets, Slides, ...) can't be fetched with
 * `alt=media`; they need an export, which we don't support. Skip them rather
 * than fail the whole upload.
 */
export const isNativeGoogleType = (mimeType?: string): boolean =>
  Boolean(mimeType) &&
  mimeType!.startsWith("application/vnd.google-apps.") &&
  mimeType !== GOOGLE_FOLDER_MIME;

/**
 * Walks a set of picked Google Drive entries, descending into folders, and
 * downloads every supported leaf file into a `PickedItem` that remembers its
 * path. Pure: the network calls are the injected `listChildren`/`downloadFile`.
 */
export const flattenDriveTree = async (
  roots: DriveEntry[],
  listChildren: (folderId: string) => Promise<DriveEntry[]>,
  downloadFile: (
    entry: DriveEntry,
    relativePath: string
  ) => Promise<File | null>,
  prefix = ""
): Promise<PickedItem[]> => {
  const nested = await Promise.all(
    roots.map(async (entry) => {
      const relativePath = joinPath(prefix, entry.name);
      if (isDriveFolder(entry)) {
        const children = await listChildren(entry.id);
        return flattenDriveTree(
          children,
          listChildren,
          downloadFile,
          relativePath
        );
      }
      if (isNativeGoogleType(entry.mimeType)) return [];
      const file = await downloadFile(entry, relativePath);
      return file ? [{ file, relativePath }] : [];
    })
  );
  return nested.flat();
};

export const isSharePointFolder = (item: FilePickerItem): boolean =>
  Boolean(item.folder);

/**
 * Walks a set of picked SharePoint/OneDrive items, descending into folders, and
 * downloads every leaf file into a `PickedItem`. Pure, like `flattenDriveTree`.
 */
export const flattenSharePointTree = async (
  roots: FilePickerItem[],
  listChildren: (item: FilePickerItem) => Promise<FilePickerItem[]>,
  downloadFile: (
    item: FilePickerItem,
    relativePath: string
  ) => Promise<File | null>,
  prefix = ""
): Promise<PickedItem[]> => {
  const nested = await Promise.all(
    roots.map(async (item) => {
      const relativePath = joinPath(prefix, item.name || "item");
      if (isSharePointFolder(item)) {
        const children = await listChildren(item);
        return flattenSharePointTree(
          children,
          listChildren,
          downloadFile,
          relativePath
        );
      }
      const file = await downloadFile(item, relativePath);
      return file ? [{ file, relativePath }] : [];
    })
  );
  return nested.flat();
};
